// The design lock for colour and type. scripts/build.ts writes these as CSS
// custom properties (dist/css/tokens.css); static/css/ternion.css only ever
// reads them through var(--name). One dark world, by choice: a table of
// slate under a night sky.

export type Hex = `#${string}`

export type PaletteConfig = {
  readonly colours: Readonly<Record<string, { readonly hex: Hex; readonly meaning: string }>>
  readonly sky: {
    readonly glowInner: Hex
    readonly glowMid: Hex
    readonly glowOuter: Hex
    readonly lattice: readonly [number, number, number] // rgb of the faint triangles
    readonly dust: readonly [number, number, number] // rgb of the drifting motes
  }
  readonly fonts: Readonly<Record<"display" | "body" | "mono", string>>
  readonly fontSheet: string
  readonly cardWidth: number // px before the table sizes it; height is 1.4 × width
}

export const PALETTE = {
  colours: {
    void: { hex: "#06070b", meaning: "the page behind everything" },
    night: { hex: "#0b0d14", meaning: "panels and the veil" },
    slate: { hex: "#141721", meaning: "a card's face" },
    "slate-hi": { hex: "#1e2230", meaning: "a card's lit edge" },
    line: { hex: "#2a2f3f", meaning: "quiet lines: slots, grids, borders" },
    bone: { hex: "#e6dcc6", meaning: "still: a card whose outer ring is level; plain text" },
    ember: { hex: "#efb25c", meaning: "rise: outer ring up; gold for a rite won unaided" },
    tide: { hex: "#70d0c4", meaning: "sink: outer ring down; mirrors and tides" },
    whole: { hex: "#f4ecff", meaning: "whole: a folded card, the Monad" },
    rust: { hex: "#e0604f", meaning: "a broken law: the rings that refused" },
    mute: { hex: "#8a8676", meaning: "hints and labels that should not shout" },
  },
  sky: {
    glowInner: "#11141f",
    glowMid: "#090a11",
    glowOuter: "#040408",
    lattice: [150, 160, 200],
    dust: [230, 220, 198],
  },
  fonts: {
    display: "'Cormorant Garamond', 'Cormorant', Garamond, 'Times New Roman', serif",
    body: "'Spectral', Georgia, 'Times New Roman', serif",
    mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
  },
  fontSheet:
    "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Spectral:ital,wght@0,300;0,400;1,300&family=JetBrains+Mono:wght@400&display=swap",
  cardWidth: 112,
} as const satisfies PaletteConfig

const colourLine = ([name, c]: readonly [string, { readonly hex: Hex }]): string => `  --${name}: ${c.hex};`

// The CSS custom properties, as the build writes them.
export const tokensCss = (): string =>
  [
    ":root {",
    "  color-scheme: dark;",
    ...Object.entries(PALETTE.colours).map(colourLine),
    `  --font-display: ${PALETTE.fonts.display};`,
    `  --font-body: ${PALETTE.fonts.body};`,
    `  --font-mono: ${PALETTE.fonts.mono};`,
    `  --cw: ${PALETTE.cardWidth}px;`,
    "}",
    "",
  ].join("\n")
